/**
 * Clickable is a component wrapper that applies attributes to elements to make
 * them accessibly clickable. This includes adding a tabindex, a role, and
 * keypress handler. You should use this component if you are making a non-
 * interactive element interactive, e.g. div, svg, span, etc.
 */

import isPropValid from '@emotion/is-prop-valid';
import React, { createElement, ReactEventHandler } from 'react';
import type { AnalyticsAttributes } from './contracts';

const dummyFn = () => {};

const onKeyClick = (fn: ReactEventHandler) => (e: React.KeyboardEvent) => {
  if (e.key === 'Enter' || e.key === ' ') {
    fn(e);
  }
};

type ClickableElement = {
  /**
   * ID to pass to analytics to describe the element that was clicked.
   */
  'data-analytics-id': string | null;
  'data-analytics-attributes'?: string | AnalyticsAttributes;
  'data-native-payload'?: string | Record<string, any>;
};

type OwnProps<T> = T &
  React.HTMLProps<HTMLElement | SVGElement> &
  ClickableElement & {
    /**
     * Element that should receive accessibility attributes. Pass either a string
     * representation of an HTML element (e.g. 'div') or a React component that is
     * configured to accept the attributes.
     */
    element?: any;

    /**
     * Ref to apply to element.
     */
    elementRef?: React.RefObject<any>;

    /**
     * Do not pass `ref` to component, instead set `elementRef`.
     */
    ref?: never;
  };

const Clickable: <T = {}>(p: OwnProps<T>) => React.ReactElement = ({
  children,
  disabled,
  onClick = dummyFn,
  role = 'button',
  tabIndex = 0,
  element: Element = 'div',
  elementRef,
  ...props
}) => {
  const propsToCheck = {
    'aria-disabled': disabled || undefined,
    onClick,
    onKeyPress: onKeyClick(onClick),
    ref: elementRef,
    role,
    tabIndex,
    ...props
  };

  const propsForElement =
    typeof Element === 'string'
      ? Object.entries(propsToCheck).reduce(
          (acc, [key, value]) => {
            if (isPropValid(key)) acc[key] = value;

            return acc;
          },
          {} as Record<string, any>
        )
      : propsToCheck;

  if (
    'data-analytics-attributes' in propsForElement &&
    typeof propsForElement['data-analytics-attributes'] !== 'string'
  ) {
    propsForElement['data-analytics-attributes'] = JSON.stringify(
      propsForElement['data-analytics-attributes']
    );
  }

  if (
    'data-native-payload' in propsForElement &&
    typeof propsForElement['data-native-payload'] !== 'string'
  ) {
    propsForElement['data-native-payload'] = JSON.stringify(
      propsForElement['data-native-payload']
    );
  }

  return createElement(Element, propsForElement, children);
};

export default Clickable;
